import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
    Alert,
    App,
    Breadcrumb,
    Button,
    Card,
    Col,
    Descriptions,
    Drawer,
    Empty,
    Form,
    Input,
    InputNumber,
    List,
    Modal,
    Row,
    Select,
    Space,
    Statistic,
    Switch,
    Table,
    Tabs,
    Tag,
    Tooltip,
    Typography,
} from "antd";
import {
    CheckCircleOutlined,
    EditOutlined,
    ExperimentOutlined,
    MailOutlined,
    PlusOutlined,
    ReloadOutlined,
    SettingOutlined,
    StopOutlined,
} from "@ant-design/icons";
import dayjs from "dayjs";
import relativeTime from "dayjs/plugin/relativeTime";
import "dayjs/locale/zh-cn";

import { useAuth } from "../../../hooks/useAuth";
import {
    createClassificationRule,
    listClassificationRules,
    listMailboxes,
    listOrganizations,
    setClassificationRuleEnabled,
    testClassificationRule,
    updateClassificationRule,
} from "../../../api/v3/mailHubApi";
import {
    formatMailHubError,
    readMailHubData,
    validateClassificationRulesPayload,
    validateClassificationRuleTestPayload,
    validateMailboxListPayload,
    validateOrganizationsPayload,
} from "../lib/contracts";

dayjs.extend(relativeTime);
dayjs.locale("zh-cn");

const { Title, Text } = Typography;

const SCOPE_OPTIONS = [
    { label: "全局", value: "global" },
    { label: "组织", value: "organization" },
    { label: "邮箱", value: "mailbox" },
];

const FIELD_OPTIONS = [
    { label: "主题", value: "subject" },
    { label: "发件人地址", value: "from_address" },
    { label: "发件显示名", value: "from_display" },
    { label: "收件人地址", value: "to_address" },
    { label: "正式附件文件名", value: "attachment_filename" },
    { label: "是否有正式附件", value: "has_attachment" },
];

const STRING_OPERATOR_OPTIONS = [
    { label: "包含", value: "contains" },
    { label: "不包含", value: "not_contains" },
    { label: "等于", value: "equals" },
    { label: "不等于", value: "not_equals" },
    { label: "开头匹配", value: "starts_with" },
    { label: "结尾匹配", value: "ends_with" },
    { label: "正则", value: "regex" },
    { label: "包含任一值", value: "contains_any" },
    { label: "等于任一值", value: "equals_any" },
];

const BOOLEAN_OPERATOR_OPTIONS = [
    { label: "为真", value: "is_true" },
    { label: "为假", value: "is_false" },
];

const MODE_OPTIONS = [
    { label: "确定性处理", value: "deterministic" },
    { label: "AI 提议处理", value: "ai_assisted" },
    { label: "人工协作处理", value: "manual" },
];

const RULE_EDITOR_TABS = {
    BASIC: "basic",
    CONDITIONS: "conditions",
    ACTION: "action",
};

// 规则编辑器的必填项分散在多个标签页；校验失败时据此把用户带到首个出错字段所在标签页，
// 避免必填项藏在未激活（懒渲染）标签页里被前端跳过、最终被后端打回。
const RULE_FIELD_TAB_MAP = {
    name: RULE_EDITOR_TABS.BASIC,
    description: RULE_EDITOR_TABS.BASIC,
    priority: RULE_EDITOR_TABS.BASIC,
    is_enabled: RULE_EDITOR_TABS.BASIC,
    exclude_from_secondary: RULE_EDITOR_TABS.BASIC,
    scope_type: RULE_EDITOR_TABS.BASIC,
    organization_id: RULE_EDITOR_TABS.BASIC,
    mailbox_id: RULE_EDITOR_TABS.BASIC,
    conditions: RULE_EDITOR_TABS.CONDITIONS,
    action: RULE_EDITOR_TABS.ACTION,
};

function isBooleanField(field) {
    return field === "has_attachment";
}

function defaultOperatorForField(field) {
    return isBooleanField(field) ? "is_true" : "contains";
}

function getFieldLabel(field) {
    const option = FIELD_OPTIONS.find((item) => item.value === field);
    return option ? option.label : `未知字段：${field}`;
}

function getOperatorLabel(operator) {
    const option = [...STRING_OPERATOR_OPTIONS, ...BOOLEAN_OPERATOR_OPTIONS].find((item) => item.value === operator);
    return option ? option.label : `未知操作符：${operator}`;
}

function parseListValue(value) {
    if (!value) {
        return [];
    }

    return String(value)
        .split(/[\n,，]/)
        .map((item) => item.trim())
        .filter(Boolean);
}

function serializeConditionValue(condition) {
    if (!condition || condition.operator === "is_true" || condition.operator === "is_false") {
        return undefined;
    }

    if (condition.operator === "contains_any" || condition.operator === "equals_any") {
        return parseListValue(condition.value_input);
    }

    return condition.value_input?.trim() || null;
}

function buildRulePayload(values) {
    const conditions = (values.conditions || []).map((condition, index) => ({
        condition_group: Number(condition.condition_group || 1),
        sort_order: Number(condition.sort_order || index + 1),
        field: condition.field,
        operator: condition.operator,
        value: serializeConditionValue(condition),
    }));

    return {
        name: values.name?.trim(),
        description: values.description?.trim() || null,
        priority: Number(values.priority || 100),
        is_enabled: values.is_enabled ?? true,
        exclude_from_secondary: values.exclude_from_secondary === true,
        scope_type: values.scope_type,
        organization_id: values.scope_type === "organization" ? values.organization_id || null : null,
        mailbox_id: values.scope_type === "mailbox" ? values.mailbox_id || null : null,
        conditions,
        action: {
            category: values.action?.category?.trim(),
            sub_category: values.action?.sub_category?.trim() || null,
            suggested_queue: values.action?.suggested_queue?.trim() || null,
            suggested_mode: values.action?.suggested_mode,
            confidence:
                values.action?.confidence === undefined || values.action?.confidence === null || values.action?.confidence === ""
                    ? null
                    : Number(values.action.confidence),
        },
    };
}

function buildRuleFormValues(definition) {
    if (!definition) {
        return {
            name: "",
            description: "",
            priority: 100,
            is_enabled: true,
            exclude_from_secondary: false,
            scope_type: "global",
            organization_id: null,
            mailbox_id: null,
            conditions: [
                {
                    condition_group: 1,
                    sort_order: 1,
                    field: "subject",
                    operator: "contains",
                    value_input: "",
                },
            ],
        action: {
            category: "general",
            sub_category: "",
            suggested_queue: "general_inbox",
                suggested_mode: "manual",
                confidence: 0.3,
            },
        };
    }

    return {
        name: definition.rule.name,
        description: definition.rule.description || "",
        priority: definition.rule.priority,
        is_enabled: definition.rule.is_enabled,
        exclude_from_secondary: definition.rule.metadata?.exclude_from_secondary === true,
        scope_type: definition.rule.scope_type,
        organization_id: definition.rule.organization_id,
        mailbox_id: definition.rule.mailbox_id,
        conditions: definition.conditions.map((condition) => ({
            condition_group: condition.condition_group,
            sort_order: condition.sort_order,
            field: condition.field,
            operator: condition.operator,
            value_input: Array.isArray(condition.value)
                ? condition.value.join("\n")
                : condition.value ?? "",
        })),
        action: {
            category: definition.action.category,
            sub_category: definition.action.sub_category || "",
            suggested_queue: definition.action.suggested_queue || "",
            suggested_mode: definition.action.suggested_mode,
            confidence: definition.action.confidence,
        },
    };
}

function renderScopeTag(rule, organizationMap, mailboxMap) {
    if (rule.scope_type === "global") {
        return <Tag color="blue">全局</Tag>;
    }
    if (rule.scope_type === "organization") {
        return <Tag color="gold">组织：{organizationMap[rule.organization_id] || rule.organization_id}</Tag>;
    }
    if (rule.scope_type === "mailbox") {
        return <Tag color="purple">邮箱：{mailboxMap[rule.mailbox_id] || rule.mailbox_id}</Tag>;
    }
    return <Tag color="error">未知作用范围：{rule.scope_type}</Tag>;
}

function formatConditionPreview(condition) {
    const prefix = `G${condition.condition_group} · ${getFieldLabel(condition.field)} · ${getOperatorLabel(condition.operator)}`;
    if (condition.operator === "is_true") {
        return `${prefix} · 是`;
    }
    if (condition.operator === "is_false") {
        return `${prefix} · 否`;
    }

    if (Array.isArray(condition.value)) {
        return `${prefix} · ${condition.value.join(" / ")}`;
    }
    return `${prefix} · ${condition.value || "—"}`;
}

function formatSecondarySummary(result) {
    if (result.secondary_results.length === 0) {
        return "无附加分类";
    }
    return result.secondary_results
        .map((item) => item.sub_category ? `${item.category}/${item.sub_category}` : item.category)
        .join("，");
}

const RuleTestModal = ({ open, onClose, onRunTest, loading, contextTitle, result, error, initialTarget }) => {
    const [form] = Form.useForm();

    useEffect(() => {
        if (!open) {
            return;
        }

        form.setFieldsValue({
            receipt_id: "",
            subject: "",
            from_address: "",
            from_display: "",
            to_addresses_text: "",
            attachment_filenames_text: "",
            has_attachments: false,
            organization_id: initialTarget?.organization_id || null,
            mailbox_id: initialTarget?.mailbox_id || null,
        });
    }, [form, initialTarget, open]);

    const handleSubmit = async () => {
        const values = await form.validateFields();
        await onRunTest({
            receipt_id: values.receipt_id?.trim() || undefined,
            sample_input: {
                subject: values.subject?.trim() || null,
                from_address: values.from_address?.trim() || null,
                from_display: values.from_display?.trim() || null,
                to_addresses: parseListValue(values.to_addresses_text),
                attachment_filenames: parseListValue(values.attachment_filenames_text),
                has_attachments: values.has_attachments,
                organization_id: initialTarget?.organization_id || null,
                mailbox_id: initialTarget?.mailbox_id || null,
            },
        });
    };

    return (
        <Modal
            title={`规则测试 - ${contextTitle}`}
            open={open}
            onCancel={onClose}
            onOk={handleSubmit}
            confirmLoading={loading}
            okText="执行测试"
            cancelText="关闭"
            width={900}
            destroyOnClose
        >
            <Row gutter={16}>
                <Col span={12}>
                    <Form form={form} layout="vertical">
                        <Alert
                            type="info"
                            showIcon
                            style={{ marginBottom: 16 }}
                            message="可直接输入样例；若填写 Receipt ID，则优先用现有邮件进行测试。"
                        />
                        <Form.Item name="receipt_id" label="现有邮件 Receipt ID">
                            <Input placeholder="可选，填写后将从现有邮件构建测试输入" />
                        </Form.Item>
                        <Form.Item name="subject" label="主题">
                            <Input placeholder="样例主题" />
                        </Form.Item>
                        <Form.Item name="from_address" label="发件人地址">
                            <Input placeholder="ops@example.com" />
                        </Form.Item>
                        <Form.Item name="from_display" label="发件显示名">
                            <Input placeholder="示例显示名" />
                        </Form.Item>
                        <Form.Item name="to_addresses_text" label="收件人地址">
                            <Input.TextArea rows={2} placeholder="一行一个，或用逗号分隔" />
                        </Form.Item>
                        <Form.Item name="attachment_filenames_text" label="正式附件文件名">
                            <Input.TextArea rows={2} placeholder="一行一个，或用逗号分隔（不含内嵌图片）" />
                        </Form.Item>
                        <Form.Item name="has_attachments" label="有正式附件" valuePropName="checked">
                            <Switch />
                        </Form.Item>
                    </Form>
                </Col>
                <Col span={12}>
                    {error ? (
                        <Alert type="error" showIcon message="规则测试结果不可用" description={error} />
                    ) : !result ? (
                        <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="执行一次测试后查看命中结果与评估轨迹" />
                    ) : (
                        <Space direction="vertical" style={{ width: "100%" }} size={16}>
                            <Alert
                                type={result.result.primary_result.rule_id ? "success" : "warning"}
                                showIcon
                                message={result.result.primary_result.rule_id ? `主分类命中：${result.result.primary_result.rule_name}` : "未命中显式主分类规则，已回退默认结果"}
                                description={`主分类输出：${result.result.primary_result.category} / ${result.result.primary_result.suggested_mode} / ${result.result.primary_result.suggested_queue || "未指定队列"}`}
                            />
                            <Descriptions bordered size="small" column={1} title="双层分类结果">
                                <Descriptions.Item label="主分类">
                                    {result.result.primary_result.sub_category
                                        ? `${result.result.primary_result.category} / ${result.result.primary_result.sub_category}`
                                        : result.result.primary_result.category}
                                </Descriptions.Item>
                                <Descriptions.Item label="附加分类">
                                    {formatSecondarySummary(result.result)}
                                </Descriptions.Item>
                            </Descriptions>
                            <Descriptions bordered size="small" column={1} title="解析后的测试输入">
                                <Descriptions.Item label="主题">{result.input.subject || "—"}</Descriptions.Item>
                                <Descriptions.Item label="发件人">{result.input.from_address || "—"}</Descriptions.Item>
                                <Descriptions.Item label="发件显示名">{result.input.from_display || "—"}</Descriptions.Item>
                                <Descriptions.Item label="收件人">{result.input.to_addresses?.join("，") || "—"}</Descriptions.Item>
                                <Descriptions.Item label="正式附件">{result.input.attachment_filenames?.join("，") || "—"}</Descriptions.Item>
                                <Descriptions.Item label="有正式附件">{result.input.has_attachments ? "是" : "否"}</Descriptions.Item>
                            </Descriptions>
                            <Card size="small" title="评估轨迹">
                                <List
                                    size="small"
                                    dataSource={result.result.primary_result.trace}
                                    locale={{ emptyText: "本次测试没有可展示的轨迹" }}
                                    renderItem={(item) => (
                                        <List.Item>
                                            <Space wrap>
                                                <Tag color={item.matched ? "success" : "default"}>{item.matched ? "命中" : "未命中"}</Tag>
                                                <Text strong>{item.rule_name}</Text>
                                                <Tag>优先级 {item.priority}</Tag>
                                                <Tag>{item.scope_type}</Tag>
                                                {item.matched ? <Tag color="success">进入命中集合</Tag> : null}
                                            </Space>
                                        </List.Item>
                                    )}
                                />
                            </Card>
                            {result.result.secondary_results.length ? (
                                <Card size="small" title="附加分类命中">
                                    <List
                                        size="small"
                                        dataSource={result.result.secondary_results}
                                        renderItem={(item) => (
                                            <List.Item>
                                                <Space wrap>
                                                    <Tag color="purple">附加分类</Tag>
                                                    <Text strong>{item.rule_name || "未命名规则"}</Text>
                                                    <Tag>{item.category}{item.sub_category ? ` / ${item.sub_category}` : ""}</Tag>
                                                    <Tag>{item.confidence}</Tag>
                                                </Space>
                                            </List.Item>
                                        )}
                                    />
                                </Card>
                            ) : null}
                        </Space>
                    )}
                </Col>
            </Row>
        </Modal>
    );
};

const RuleEditorDrawer = ({
    open,
    mode,
    definition,
    organizations,
    mailboxes,
    onClose,
    onSubmit,
    onTestDraft,
    loading,
    canManage,
}) => {
    const [form] = Form.useForm();
    const [activeTabKey, setActiveTabKey] = useState(RULE_EDITOR_TABS.BASIC);
    const scopeType = Form.useWatch("scope_type", form);
    const conditions = Form.useWatch("conditions", form) || [];

    useEffect(() => {
        if (!open) {
            return;
        }
        setActiveTabKey(RULE_EDITOR_TABS.BASIC);
        form.setFieldsValue(buildRuleFormValues(definition));
    }, [definition, form, open]);

    const mailboxOptions = useMemo(() => {
        return mailboxes.map((item) => ({
            label: item.display_name
                ? `${item.display_name} <${item.email_address}>`
                : item.email_address,
            value: item.id,
        }));
    }, [mailboxes]);

    const resolveValidatedPayload = async () => {
        try {
            const values = await form.validateFields();
            return buildRulePayload(values);
        } catch (error) {
            const firstErrorField = error?.errorFields?.[0]?.name?.[0];
            const targetTab = RULE_FIELD_TAB_MAP[firstErrorField];
            if (targetTab) {
                setActiveTabKey(targetTab);
            }
            return null;
        }
    };

    const handleSubmit = async () => {
        const payload = await resolveValidatedPayload();
        if (payload) {
            await onSubmit(payload);
        }
    };

    const handleTestDraft = async () => {
        const payload = await resolveValidatedPayload();
        if (payload) {
            onTestDraft(payload);
        }
    };

    const handleConditionFieldChange = (conditionIndex, nextField) => {
        const current = form.getFieldValue(["conditions", conditionIndex]) || {};
        const nextIsBoolean = isBooleanField(nextField);
        const operatorFitsNextField = nextIsBoolean
            ? BOOLEAN_OPERATOR_OPTIONS.some((item) => item.value === current.operator)
            : STRING_OPERATOR_OPTIONS.some((item) => item.value === current.operator);

        if (operatorFitsNextField) {
            if (nextIsBoolean) {
                form.setFieldValue(["conditions", conditionIndex, "value_input"], undefined);
            }
            return;
        }

        form.setFieldValue(["conditions", conditionIndex], {
            ...current,
            field: nextField,
            operator: defaultOperatorForField(nextField),
            value_input: nextIsBoolean ? undefined : "",
        });
    };

    return (
        <Drawer
            title={mode === "edit" ? "编辑分类规则" : "新建分类规则"}
            width={980}
            open={open}
            onClose={onClose}
            destroyOnClose
            extra={
                <Space>
                    <Button onClick={onClose}>取消</Button>
                    <Button icon={<ExperimentOutlined />} onClick={handleTestDraft} disabled={!canManage}>
                        测试当前规则
                    </Button>
                    <Button type="primary" loading={loading} onClick={handleSubmit} disabled={!canManage}>
                        {mode === "edit" ? "保存" : "创建"}
                    </Button>
                </Space>
            }
        >
            <Form form={form} layout="vertical" disabled={!canManage}>
                <Alert
                    type="info"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="当前分类治理已升级为双层模型：主分类负责主路由，附加分类负责补充语义。"
                />

                <Tabs
                    activeKey={activeTabKey}
                    onChange={setActiveTabKey}
                    items={[
                        {
                            key: "basic",
                            label: "基础信息",
                            forceRender: true,
                            children: (
                                <>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="name" label="规则名称" rules={[{ required: true, message: "请输入规则名称" }]}>
                                                <Input placeholder="例如：MVT 报文识别" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item name="priority" label="优先级" rules={[{ required: true, message: "请输入优先级" }]}>
                                                <InputNumber min={1} max={9999} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={6}>
                                            <Form.Item name="scope_type" label="作用范围" rules={[{ required: true, message: "请选择作用范围" }]}>
                                                <Select options={SCOPE_OPTIONS} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Form.Item name="description" label="规则说明">
                                        <Input.TextArea rows={3} placeholder="说明这条规则为什么存在、用于判断什么邮件" />
                                    </Form.Item>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name="organization_id" label="组织范围" rules={scopeType === "organization" ? [{ required: true, message: "请选择组织" }] : []}>
                                                <Select allowClear disabled={scopeType !== "organization"} options={organizations.map((item) => ({ label: item.name, value: item.id }))} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name="mailbox_id" label="邮箱范围" rules={scopeType === "mailbox" ? [{ required: true, message: "请选择邮箱" }] : []}>
                                                <Select allowClear showSearch disabled={scopeType !== "mailbox"} options={mailboxOptions} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Space size="large">
                                        <Form.Item name="is_enabled" label="启用规则" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch />
                                        </Form.Item>
                                        <Form.Item name="exclude_from_secondary" label="不进入附加分类" valuePropName="checked" style={{ marginBottom: 0 }}>
                                            <Switch />
                                        </Form.Item>
                                        <Form.Item label="主分类选取方式" style={{ marginBottom: 0 }}>
                                            <Tag color="processing">按优先级/范围/创建时间选主分类</Tag>
                                        </Form.Item>
                                    </Space>
                                </>
                            ),
                        },
                        {
                            key: "conditions",
                            label: "匹配条件",
                            forceRender: true,
                            children: (
                                <>
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                        message="分类条件允许主题、发件人、收件人与正式附件信号；正文（含引用历史）与内嵌图片不参与分类，避免已知误伤。同组按 AND、组间按 OR 计算。「正式附件文件名不包含/不等于」仅在确有正式附件时生效，无附件邮件不会因此命中。"
                                    />
                                    <Form.List name="conditions">
                                        {(fields, { add, remove }) => (
                                            <Space direction="vertical" size={12} style={{ width: "100%" }}>
                                                {fields.map((field) => {
                                                    const condition = conditions[field.name] || {};
                                                    const fieldTypeBoolean = isBooleanField(condition.field);
                                                    const operatorOptions = fieldTypeBoolean ? BOOLEAN_OPERATOR_OPTIONS : STRING_OPERATOR_OPTIONS;
                                                    const multiValue = condition.operator === "contains_any" || condition.operator === "equals_any";

                                                    return (
                                                        <Card
                                                            key={field.key}
                                                            size="small"
                                                            title={`分支 ${condition.condition_group || 1} · 条件 ${field.name + 1}`}
                                                            extra={fields.length > 1 ? <Button danger type="link" onClick={() => remove(field.name)}>删除</Button> : null}
                                                        >
                                                            <Row gutter={12}>
                                                                <Col span={4}>
                                                                    <Form.Item name={[field.name, "condition_group"]} label="分组" rules={[{ required: true, message: "请输入分组" }]}>
                                                                        <InputNumber min={1} style={{ width: "100%" }} />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col span={4}>
                                                                    <Form.Item name={[field.name, "sort_order"]} label="组内顺序" rules={[{ required: true, message: "请输入顺序" }]}>
                                                                        <InputNumber min={1} style={{ width: "100%" }} />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col span={6}>
                                                                    <Form.Item name={[field.name, "field"]} label="字段" rules={[{ required: true, message: "请选择字段" }]}>
                                                                        <Select
                                                                            options={FIELD_OPTIONS}
                                                                            onChange={(nextField) => handleConditionFieldChange(field.name, nextField)}
                                                                        />
                                                                    </Form.Item>
                                                                </Col>
                                                                <Col span={10}>
                                                                    <Form.Item name={[field.name, "operator"]} label="操作符" rules={[{ required: true, message: "请选择操作符" }]}>
                                                                        <Select options={operatorOptions} />
                                                                    </Form.Item>
                                                                </Col>
                                                            </Row>
                                                            {!fieldTypeBoolean ? (
                                                                <Form.Item name={[field.name, "value_input"]} label="匹配值" rules={[{ required: true, message: "请输入匹配值" }]}>
                                                                    {multiValue ? (
                                                                        <Input.TextArea rows={3} placeholder="多值时一行一个，或用逗号分隔" />
                                                                    ) : (
                                                                        <Input placeholder="输入匹配值" />
                                                                    )}
                                                                </Form.Item>
                                                            ) : null}
                                                        </Card>
                                                    );
                                                })}

                                                <Space>
                                                    <Button
                                                        type="dashed"
                                                        onClick={() => add({
                                                            condition_group: fields.length > 0 ? (conditions[fields.length - 1]?.condition_group || 1) : 1,
                                                            sort_order: fields.length + 1,
                                                            field: "subject",
                                                            operator: "contains",
                                                            value_input: "",
                                                        })}
                                                    >
                                                        新增同组条件
                                                    </Button>
                                                    <Button
                                                        type="dashed"
                                                        onClick={() => add({
                                                            condition_group: Math.max(0, ...conditions.map((item) => Number(item?.condition_group || 0))) + 1,
                                                            sort_order: 1,
                                                            field: "subject",
                                                            operator: "contains",
                                                            value_input: "",
                                                        })}
                                                    >
                                                        新增 OR 分支
                                                    </Button>
                                                </Space>
                                            </Space>
                                        )}
                                    </Form.List>
                                </>
                            ),
                        },
                        {
                            key: "action",
                            label: "命中结果",
                            forceRender: true,
                            children: (
                                <>
                                    <Alert
                                        type="info"
                                        showIcon
                                        style={{ marginBottom: 16 }}
                                        message="所有命中规则都会参与结果归集；命中集合中的首条规则会成为主分类，其余命中规则会沉淀为附加分类。"
                                    />
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name={["action", "category"]} label="分类" rules={[{ required: true, message: "请输入分类" }]}>
                                                <Input placeholder="例如：mvt" />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name={["action", "sub_category"]} label="子分类">
                                                <Input placeholder="可选" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name={["action", "suggested_mode"]} label="主路由建议处理模式" rules={[{ required: true, message: "请选择处理模式" }]}>
                                                <Select options={MODE_OPTIONS} />
                                            </Form.Item>
                                        </Col>
                                        <Col span={12}>
                                            <Form.Item name={["action", "suggested_queue"]} label="主路由建议队列">
                                                <Input placeholder="例如：aviation_ops" />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                    <Row gutter={16}>
                                        <Col span={12}>
                                            <Form.Item name={["action", "confidence"]} label="置信度">
                                                <InputNumber min={0} max={1} step={0.1} style={{ width: "100%" }} />
                                            </Form.Item>
                                        </Col>
                                    </Row>
                                </>
                            ),
                        },
                    ]}
                />
            </Form>
        </Drawer>
    );
};

const ClassificationGovernancePage = () => {
    const auth = useAuth();
    const { message } = App.useApp();

    const canManage = auth.hasAnyPermission([
        "mail_hub:classification_manage",
        "mail_hub:manage",
    ]);

    const [loading, setLoading] = useState(false);
    const [saving, setSaving] = useState(false);
    const [rules, setRules] = useState([]);
    const [organizations, setOrganizations] = useState([]);
    const [mailboxes, setMailboxes] = useState([]);
    const [loadError, setLoadError] = useState(null);
    const [filters, setFilters] = useState({ query: "", scopeType: undefined, enabled: undefined });
    const [editorState, setEditorState] = useState({ open: false, mode: "create", definition: null });
    const [testState, setTestState] = useState({ open: false, contextTitle: "活动规则链", target: null, result: null, error: null, loading: false });

    const loadData = useCallback(async () => {
        setLoading(true);
        try {
            const [rulesRes, orgRes, mailboxRes] = await Promise.all([
                listClassificationRules(),
                listOrganizations(),
                listMailboxes(),
            ]);
            setRules(validateClassificationRulesPayload(readMailHubData(rulesRes, "分类规则")));
            setOrganizations(validateOrganizationsPayload(readMailHubData(orgRes, "组织列表")));
            setMailboxes(validateMailboxListPayload(readMailHubData(mailboxRes, "收件箱列表")));
            setLoadError(null);
        } catch (error) {
            const errorMessage = formatMailHubError(error, "加载分类治理数据失败");
            setRules([]);
            setOrganizations([]);
            setMailboxes([]);
            setLoadError(errorMessage);
            message.error(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [message]);

    useEffect(() => {
        loadData();
    }, [loadData]);

    const organizationMap = useMemo(() => {
        const map = {};
        organizations.forEach((item) => {
            map[item.id] = item.name;
        });
        return map;
    }, [organizations]);

    const mailboxMap = useMemo(() => {
        const map = {};
        mailboxes.forEach((item) => {
            map[item.id] = item.display_name
                ? `${item.display_name} <${item.email_address}>`
                : item.email_address;
        });
        return map;
    }, [mailboxes]);

    const filteredRules = useMemo(() => {
        return rules.filter((item) => {
            const query = filters.query.trim().toLowerCase();
            const matchesQuery = !query
                || item.rule.name.toLowerCase().includes(query)
                || (item.rule.description || "").toLowerCase().includes(query)
                || item.action.category.toLowerCase().includes(query);
            const matchesScope = !filters.scopeType || item.rule.scope_type === filters.scopeType;
            const matchesEnabled =
                filters.enabled === undefined
                    ? true
                    : item.rule.is_enabled === filters.enabled;
            return matchesQuery && matchesScope && matchesEnabled;
        });
    }, [filters, rules]);

    const stats = useMemo(() => {
        return {
            total: rules.length,
            enabled: rules.filter((item) => item.rule.is_enabled).length,
            scoped: rules.filter((item) => item.rule.scope_type !== "global").length,
            manual: rules.filter((item) => item.action.suggested_mode === "manual").length,
        };
    }, [rules]);

    const handleSave = async (payload) => {
        setSaving(true);
        try {
            if (editorState.mode === "edit" && editorState.definition?.rule?.id) {
                await updateClassificationRule(editorState.definition.rule.id, payload);
                message.success("规则已更新");
            } else {
                await createClassificationRule(payload);
                message.success("规则已创建");
            }
            setEditorState({ open: false, mode: "create", definition: null });
            await loadData();
        } catch (error) {
            message.error(error?.response?.data?.message || "保存规则失败");
        } finally {
            setSaving(false);
        }
    };

    const handleToggleEnabled = async (definition) => {
        try {
            await setClassificationRuleEnabled(definition.rule.id, !definition.rule.is_enabled);
            message.success(definition.rule.is_enabled ? "规则已停用" : "规则已启用");
            await loadData();
        } catch (error) {
            message.error(error?.response?.data?.message || "更新规则状态失败");
        }
    };

    const openChainTestModal = () => {
        setTestState({
            open: true,
            contextTitle: "活动规则链",
            target: null,
            result: null,
            error: null,
            loading: false,
        });
    };

    const openSavedRuleTestModal = (definition) => {
        setTestState({
            open: true,
            contextTitle: definition.rule.name,
            target: {
                rule_id: definition.rule.id,
                organization_id: definition.rule.organization_id,
                mailbox_id: definition.rule.mailbox_id,
            },
            result: null,
            error: null,
            loading: false,
        });
    };

    const openDraftRuleTestModal = (draftRule) => {
        setTestState({
            open: true,
            contextTitle: draftRule.name || "当前草稿",
            target: {
                rule: draftRule,
                organization_id: draftRule.organization_id,
                mailbox_id: draftRule.mailbox_id,
            },
            result: null,
            error: null,
            loading: false,
        });
    };

    const handleRunTest = async (testInput) => {
        setTestState((current) => ({ ...current, loading: true }));
        try {
            const payload = {
                ...testInput,
                ...(testState.target?.rule_id ? { rule_id: testState.target.rule_id } : {}),
                ...(testState.target?.rule ? { rule: testState.target.rule } : {}),
            };
            const response = await testClassificationRule(payload);
            const result = validateClassificationRuleTestPayload(readMailHubData(response, "规则测试"));
            setTestState((current) => ({ ...current, result, error: null, loading: false }));
        } catch (error) {
            const errorMessage = formatMailHubError(error, "规则测试失败");
            setTestState((current) => ({ ...current, result: null, error: errorMessage, loading: false }));
            message.error(errorMessage);
        }
    };

    const columns = [
        {
            title: "规则",
            key: "rule",
            render: (_, definition) => (
                <Space direction="vertical" size={2}>
                    <Text strong>{definition.rule.name}</Text>
                    <Text type="secondary">{definition.rule.description || "—"}</Text>
                </Space>
            ),
        },
        {
            title: "作用范围",
            key: "scope",
            width: 220,
            render: (_, definition) => renderScopeTag(definition.rule, organizationMap, mailboxMap),
        },
        {
            title: "优先级",
            dataIndex: ["rule", "priority"],
            key: "priority",
            width: 100,
            sorter: (left, right) => left.rule.priority - right.rule.priority,
        },
        {
            title: "条件",
            key: "conditions",
            width: 340,
            render: (_, definition) => (
                <Space direction="vertical" size={2}>
                    <Text>{definition.conditions.length} 条</Text>
                    {definition.conditions.slice(0, 2).map((condition) => (
                        <Text type="secondary" key={condition.id || `${condition.condition_group}-${condition.sort_order}`}>
                            {formatConditionPreview(condition)}
                        </Text>
                    ))}
                    {definition.conditions.length > 2 ? <Text type="secondary">...</Text> : null}
                </Space>
            ),
        },
        {
            title: "命中动作",
            key: "action",
            width: 220,
            render: (_, definition) => (
                <Space direction="vertical" size={2}>
                    <Text>{definition.action.category}{definition.action.sub_category ? ` / ${definition.action.sub_category}` : ""}</Text>
                    <Text type="secondary">{MODE_OPTIONS.find((item) => item.value === definition.action.suggested_mode).label}</Text>
                    <Text type="secondary">队列：{definition.action.suggested_queue || "—"}</Text>
                    {definition.rule.metadata?.exclude_from_secondary ? (
                        <Tag color="purple">不进入附加分类</Tag>
                    ) : null}
                </Space>
            ),
        },
        {
            title: "状态",
            key: "status",
            width: 140,
            render: (_, definition) => (
                <Space direction="vertical" size={4}>
                    <Tag color={definition.rule.is_enabled ? "success" : "default"}>
                        {definition.rule.is_enabled ? "已启用" : "已停用"}
                    </Tag>
                    {definition.rule.metadata?.exclude_from_secondary ? <Tag color="purple">附加分类已抑制</Tag> : null}
                </Space>
            ),
        },
        {
            title: "更新时间",
            key: "updated_at",
            width: 140,
            render: (_, definition) => (
                <Tooltip title={dayjs(definition.rule.updated_at).format("YYYY-MM-DD HH:mm:ss")}>
                    <Text>{dayjs(definition.rule.updated_at).fromNow()}</Text>
                </Tooltip>
            ),
        },
        {
            title: "操作",
            key: "actions",
            width: 210,
            fixed: "right",
            render: (_, definition) => (
                <Space wrap>
                    <Button size="small" icon={<ExperimentOutlined />} onClick={() => openSavedRuleTestModal(definition)}>
                        测试
                    </Button>
                    <Button
                        size="small"
                        icon={<EditOutlined />}
                        disabled={!canManage}
                        onClick={() => setEditorState({ open: true, mode: "edit", definition })}
                    >
                        编辑
                    </Button>
                    <Button
                        size="small"
                        icon={definition.rule.is_enabled ? <StopOutlined /> : <CheckCircleOutlined />}
                        disabled={!canManage}
                        onClick={() => handleToggleEnabled(definition)}
                    >
                        {definition.rule.is_enabled ? "停用" : "启用"}
                    </Button>
                </Space>
            ),
        },
    ];

    return (
        <div>
            <Breadcrumb
                style={{ marginBottom: 12 }}
                items={[
                    { title: "Mail Hub" },
                    { title: "运维与审计" },
                    { title: "分类治理" },
                ]}
            />

            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
                <div>
                    <Title level={4} style={{ margin: 0 }}>
                        <SettingOutlined style={{ marginRight: 8 }} />
                        分类治理
                    </Title>
                    <Text type="secondary">
                        规则定义层独立治理，分类执行入口已切到数据库规则配置。
                    </Text>
                </div>
                <Space>
                    <Button icon={<ExperimentOutlined />} onClick={openChainTestModal}>
                        测试活动规则链
                    </Button>
                    <Button icon={<ReloadOutlined />} onClick={loadData} loading={loading}>
                        刷新
                    </Button>
                    <Button type="primary" icon={<PlusOutlined />} disabled={!canManage} onClick={() => setEditorState({ open: true, mode: "create", definition: null })}>
                        新建规则
                    </Button>
                </Space>
            </div>

            {loadError ? (
                <Alert
                    type="error"
                    showIcon
                    style={{ marginBottom: 16 }}
                    message="分类治理数据不可用"
                    description={loadError}
                />
            ) : null}

            {!loadError ? <Row gutter={16} style={{ marginBottom: 16 }}>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="规则总数" value={stats.total} prefix={<SettingOutlined />} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="已启用" value={stats.enabled} valueStyle={{ color: "#52c41a" }} prefix={<CheckCircleOutlined />} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="范围化规则" value={stats.scoped} valueStyle={{ color: "#1677ff" }} prefix={<MailOutlined />} />
                    </Card>
                </Col>
                <Col span={6}>
                    <Card size="small">
                        <Statistic title="人工协作出口" value={stats.manual} valueStyle={{ color: "#faad14" }} prefix={<ExperimentOutlined />} />
                    </Card>
                </Col>
            </Row> : null}

            <Card size="small" style={{ marginBottom: 16 }}>
                <Space wrap>
                    <Input.Search
                        allowClear
                        placeholder="按规则名、说明、分类搜索"
                        style={{ width: 280 }}
                        onSearch={(value) => setFilters((current) => ({ ...current, query: value || "" }))}
                    />
                    <Select
                        allowClear
                        placeholder="作用范围"
                        style={{ width: 180 }}
                        options={SCOPE_OPTIONS}
                        value={filters.scopeType}
                        onChange={(value) => setFilters((current) => ({ ...current, scopeType: value }))}
                    />
                    <Select
                        allowClear
                        placeholder="启用状态"
                        style={{ width: 180 }}
                        value={filters.enabled}
                        onChange={(value) => setFilters((current) => ({ ...current, enabled: value }))}
                        options={[
                            { label: "已启用", value: true },
                            { label: "已停用", value: false },
                        ]}
                    />
                </Space>
            </Card>

            {!loadError ? (
                <Table
                    rowKey={(definition) => definition.rule.id}
                    loading={loading}
                    columns={columns}
                    dataSource={filteredRules}
                    scroll={{ x: 1500 }}
                    pagination={{ pageSize: 20, showSizeChanger: true }}
                />
            ) : null}

            <RuleEditorDrawer
                open={editorState.open}
                mode={editorState.mode}
                definition={editorState.definition}
                organizations={organizations}
                mailboxes={mailboxes}
                onClose={() => setEditorState({ open: false, mode: "create", definition: null })}
                onSubmit={handleSave}
                onTestDraft={openDraftRuleTestModal}
                loading={saving}
                canManage={canManage}
            />

            <RuleTestModal
                open={testState.open}
                onClose={() => setTestState({ open: false, contextTitle: "活动规则链", target: null, result: null, error: null, loading: false })}
                onRunTest={handleRunTest}
                loading={testState.loading}
                contextTitle={testState.contextTitle}
                result={testState.result}
                error={testState.error}
                initialTarget={testState.target}
            />
        </div>
    );
};

export default ClassificationGovernancePage;
